import { TypedDocumentNode } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import {
  CloudControllerMutationsUpdateReaderFirmwareArgs,
  CloudControllerQueriesGetReaderFirmwareUpdateArgs,
  Mutation,
  Query,
  ReaderFirmwareUpdateStatus,
  Subscription
} from 'graphql/generated/graphqlTypes';
import { UPDATE_READER_FIRMWARE } from 'graphql/v2/mutations';
import { GET_READER_FIRMWARE_UPDATE } from 'graphql/v2/queries';
import { ON_READER_FIRMWARE_UPDATE } from 'graphql/v2/subscriptions';
import useGraphQLAuthToken from 'hooks/useGraphQLAuthToken';
import { useEffect, useRef, useState } from 'react';
import { isMockedMode } from 'utils/isMockedMode';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { subscribeToDocument } from 'utils/subscribeToDocument';
import { TranslatedHookProps } from '../hooks.types';

const GET_READER_FIRMWARE_UPDATE_TYPED: TypedDocumentNode<
  Pick<Query, 'cloudController'>,
  CloudControllerQueriesGetReaderFirmwareUpdateArgs
> = GET_READER_FIRMWARE_UPDATE;
const UPDATE_READER_FIRMWARE_TYPED: TypedDocumentNode<
  Pick<Mutation, 'cloudController'>,
  CloudControllerMutationsUpdateReaderFirmwareArgs
> = UPDATE_READER_FIRMWARE;

export const READER_FIRMWARE_END_STATUSES: ReadonlySet<ReaderFirmwareUpdateStatus> = new Set([
  ReaderFirmwareUpdateStatus.Succeeded,
  ReaderFirmwareUpdateStatus.Failed,
  ReaderFirmwareUpdateStatus.Rejected,
  ReaderFirmwareUpdateStatus.TimedOut,
  ReaderFirmwareUpdateStatus.Canceled,
  ReaderFirmwareUpdateStatus.Removed
]);

export interface ReaderFirmwareProgress {
  status: ReaderFirmwareUpdateStatus;
  /** 0 to 100 */
  percentage: number;
}

type Props = TranslatedHookProps & {
  doorId: string;
  onFinished: (status: ReaderFirmwareUpdateStatus) => void;
};

const useReaderFirmwareUpdate = ({ doorId, translation, handleFetchError, onFinished }: Props) => {
  const getToken = useGraphQLAuthToken();

  /** The running update, or the end status of the last one. */
  const [firmwareUpdate, setFirmwareUpdate] = useState<ReaderFirmwareProgress>();
  const unsubscribeRef = useRef<() => void>(undefined);

  const [getReaderFirmwareUpdate] = useLazyQuery(GET_READER_FIRMWARE_UPDATE_TYPED, { fetchPolicy: 'network-only' });

  const [updateReaderFirmwareMutation, { loading: requestingReaderFirmwareUpdate }] = useMutation(
    UPDATE_READER_FIRMWARE_TYPED,
    {
      onError: () => handleFetchError(translation.update_reader_firmware_error)
    }
  );

  const unsubscribe = () => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = undefined;
  };

  const applyUpdate = ({ status, percentage }: ReaderFirmwareProgress) => {
    setFirmwareUpdate({ status, percentage });
    if (!READER_FIRMWARE_END_STATUSES.has(status)) return;
    unsubscribe();
    onFinished(status);
  };

  const subscribe = () => {
    unsubscribe();
    unsubscribeRef.current = subscribeToDocument<Pick<Subscription, 'onReaderFirmwareUpdate'>>(
      ON_READER_FIRMWARE_UPDATE,
      { doorId },
      getToken,
      ({ onReaderFirmwareUpdate: update }) => update && applyUpdate(update),
      () => {
        unsubscribe();
        setFirmwareUpdate(undefined);
        handleFetchError(translation.update_reader_firmware_progress_lost);
      }
    );
  };

  const fetchFirmwareUpdate = async () => {
    try {
      const { data } = await getReaderFirmwareUpdate({ variables: { doorId } });
      return data?.cloudController.getReaderFirmwareUpdate;
    } catch {
      handleFetchError(translation.reader_firmware_status_error);
      return undefined;
    }
  };

  useEffect(() => {
    // The Amplify client bypasses Apollo's MockedProvider, so mocked modes get no progress.
    if (isMockedMode()) return;
    let isActive = true;

    const resumeProgress = async () => {
      const update = await fetchFirmwareUpdate();
      if (!isActive || !update) return;
      setFirmwareUpdate({ status: update.status, percentage: update.percentage });
      if (READER_FIRMWARE_END_STATUSES.has(update.status)) return;

      subscribe();
      // The subscription misses an event between the first query and the subscribe; this query catches it.
      const latest = await fetchFirmwareUpdate();
      if (isActive && latest && unsubscribeRef.current) applyUpdate(latest);
    };
    resumeProgress();

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [doorId]);

  const updateReaderFirmware = async (): Promise<boolean> => {
    const previous = firmwareUpdate;
    if (!isMockedMode()) {
      // Subscribe before the mutation: the API does not replay events sent before the subscribe.
      subscribe();
      setFirmwareUpdate({ status: ReaderFirmwareUpdateStatus.Queued, percentage: 0 });
    }

    const { data } = await ignoreHandledMutationError(updateReaderFirmwareMutation({ variables: { doorId } }));
    const success = !!data?.cloudController.updateReaderFirmware;
    if (!success) {
      unsubscribe();
      setFirmwareUpdate(previous);
    }
    return success;
  };

  return {
    updateReaderFirmware,
    requestingReaderFirmwareUpdate,
    firmwareUpdate,
    isFirmwareUpdateInProgress: !!firmwareUpdate && !READER_FIRMWARE_END_STATUSES.has(firmwareUpdate.status)
  };
};

export default useReaderFirmwareUpdate;
